import React from "react";
import { AlertTriangle, Info } from "lucide-react";

import { Alert } from "../../ui/shadcn/alert";
import {
  RestrictionBreakdownResponse,
  RestrictionEntry,
} from "../../../services/api";

const pct = (value: number) =>
  `${value.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

/** Restrição que não incide sobre a área — informação real ("aqui isso não é
 * problema"), diferente de ausência, então aparece atenuada em vez de sumir.
 * Vale a incidência, não `percentage`: uma restrição inteiramente sobreposta por
 * outra de maior incidência tem parcela atribuída 0 mas continua incidindo, e
 * atenuá-la diria ao usuário que ali não há problema. */
const isZero = (entry: RestrictionEntry) =>
  entry.available && entry.incidence_percentage === 0;

interface RestrictionRowProps {
  entry: RestrictionEntry;
  onHover: (entry: RestrictionEntry | null) => void;
}

function RestrictionRow({ entry, onHover }: RestrictionRowProps) {
  if (!entry.available) {
    return (
      <li className="flex items-start justify-between gap-3 py-1.5">
        <div className="flex flex-col">
          <span className="text-[12px] text-gray-500">{entry.label}</span>
          <span className="text-[10px] text-gray-400">{entry.description}</span>
        </div>
        <span className="text-[11px] font-semibold text-amber-600 shrink-0">
          indisponível
        </span>
      </li>
    );
  }

  const muted = isZero(entry);
  // Só linhas com forma respondem ao mouse: apontar uma restrição que não incide
  // aqui não teria o que destacar, e o cursor prometeria um realce que não vem.
  const highlightable = Boolean(entry.geometry);
  return (
    <li
      className={`flex items-start justify-between gap-3 py-1.5 px-1 -mx-1 rounded transition-colors ${
        highlightable ? "cursor-pointer hover:bg-amber-50" : ""
      }`}
      onMouseEnter={highlightable ? () => onHover(entry) : undefined}
      onMouseLeave={highlightable ? () => onHover(null) : undefined}
      onFocus={highlightable ? () => onHover(entry) : undefined}
      onBlur={highlightable ? () => onHover(null) : undefined}
      tabIndex={highlightable ? 0 : undefined}
    >
      <div className="flex flex-col">
        <span
          className={`text-[12px] ${muted ? "text-gray-400" : "text-gray-800"}`}
        >
          {entry.label}
        </span>
        <span className="text-[10px] text-gray-400">{entry.description}</span>
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span
          className={`text-[13px] font-bold leading-tight ${
            muted ? "text-gray-400" : "text-gray-900"
          }`}
        >
          {pct(entry.percentage)}
        </span>
        {/* A incidência só aparece quando difere da parcela atribuída — é aí que
            o usuário precisa saber que houve sobreposição com outra restrição. */}
        {!muted && entry.incidence_percentage > entry.percentage && (
          <span className="text-[10px] text-gray-400 leading-tight">
            incide sobre {pct(entry.incidence_percentage)}
          </span>
        )}
      </div>
    </li>
  );
}

interface RestrictionBreakdownProps {
  breakdown: RestrictionBreakdownResponse | null;
  isLoading: boolean;
  error: string | null;
  /** Restrição apontada na lista, para o mapa destacar a área dela. */
  onHoverRestriction?: (entry: RestrictionEntry | null) => void;
}

/**
 * Memorial de Cálculo da Área Propícia.
 *
 * Lista TODAS as restrições em vigor no nível — as que ele introduz e as
 * herdadas da cascata —, para que nenhuma parcela excluída apareça sem nome.
 * Cada percentual é a parcela EXCLUSIVA pela qual a restrição responde: terreno
 * atingido por mais de uma restrição conta uma vez só, para a de maior
 * incidência. Somados ao não-atribuído, fecham a área excluída.
 */
export function RestrictionBreakdown({
  breakdown,
  isLoading,
  error,
  onHoverRestriction,
}: RestrictionBreakdownProps) {
  const handleHover = React.useCallback(
    (entry: RestrictionEntry | null) => onHoverRestriction?.(entry),
    [onHoverRestriction]
  );

  // Some com o destaque ao desmontar: fechar o memorial com o mouse parado
  // sobre uma linha deixaria a forma pendurada no mapa sem lista que a explique.
  React.useEffect(() => () => onHoverRestriction?.(null), [onHoverRestriction]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg animate-pulse">
        <div className="h-3 bg-gray-300 rounded w-1/2" />
        <div className="h-3 bg-gray-200 rounded w-full" />
        <div className="h-3 bg-gray-200 rounded w-5/6" />
        <div className="h-3 bg-gray-200 rounded w-2/3" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        variant="warning"
        icon={<AlertTriangle size={14} />}
        className="text-[11px] leading-relaxed"
      >
        {error}
      </Alert>
    );
  }

  if (!breakdown) return null;

  // Lista vazia é o mesmo estado que lista toda zerada: nada incide sobre a
  // área. Sem isso, o Teórico renderizaria um memorial em branco.
  const allZero =
    breakdown.restrictions.every(isZero) &&
    breakdown.unattributed_percentage <= 0;

  return (
    <div className="flex flex-col gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <span className="text-[11px] text-gray-500 leading-snug">
        Memorial de cálculo — restrições do potencial {breakdown.level_label}
      </span>

      {allZero ? (
        <span className="text-[12px] text-gray-800">
          Nenhuma restrição incide sobre esta área.
        </span>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-200 m-0 p-0 list-none">
          {breakdown.restrictions.map((entry) => (
            <RestrictionRow key={entry.id} entry={entry} onHover={handleHover} />
          ))}

          {breakdown.unattributed_percentage > 0 && (
            <li className="flex items-start justify-between gap-3 py-1.5">
              <div className="flex flex-col">
                <span className="text-[12px] text-amber-700">
                  Não atribuído
                </span>
                <span className="text-[10px] text-gray-400">
                  Área excluída que não pôde ser associada a uma restrição
                </span>
              </div>
              <span className="text-[13px] font-bold text-amber-700 leading-tight shrink-0">
                {pct(breakdown.unattributed_percentage)}
              </span>
            </li>
          )}
        </ul>
      )}

      <span className="text-[10px] text-gray-400 leading-relaxed">
        Percentuais sobre a área total selecionada, incluindo as restrições
        herdadas dos níveis anteriores. Terreno atingido por mais de uma
        restrição é contado uma única vez, na restrição de maior incidência — por
        isso as linhas somam a área excluída sem dupla contagem. Passe o mouse
        sobre uma linha para ver a área dela no mapa.
      </span>

      {breakdown.warning && (
        <Alert
          variant="warning"
          icon={<Info size={14} />}
          className="text-[11px] leading-relaxed"
        >
          {breakdown.warning}
        </Alert>
      )}
    </div>
  );
}

export default RestrictionBreakdown;
